import React, { useCallback, useEffect, useMemo, useState } from "react";
import { leafGroupHeader } from "@common/constants/domains/accounting/accountGroups";
import { displayAccountLabel } from "@common/helpers/accounting";
import { Account } from "@common/types/domains/accounting/account";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useAccounts } from "@hooks/useAccounts";
import { PlusIcon } from "@icons";
import { useOverlays } from "contexts/OverlayContext";
import type { AccountComboboxProps } from "./AccountCombobox.types";
import { groupAccounts, matchesAccountSearch } from "./accountComboboxGroups";
import {
    buildCreateAccountRequest,
    isAccountTypeAllowed,
} from "./accountComboboxCreateRequest";

/**
 * Chart of accounts picker for one entity: a searchable list grouped by
 * account type (name or code match), optional "Revert to default" / "Clear
 * selection" rows and a create-account footer. Renders an editable table
 * cell trigger unless `trigger` supplies another Picker trigger part.
 */
export const AccountCombobox: React.FC<AccountComboboxProps> = ({
    selectedAccount,
    onChange,
    entityId,
    readOnly,
    disabled,
    ledgerBasis = AccountingLedgerBasis.cash,
    showAccountCode = false,
    addNewAccountButton = true,
    tooltip,
    canRevertToDefault = false,
    allowedAccountTypes,
    allowedAccountIds,
    suggestedType,
    canClear = false,
    open: controlledOpen,
    onOpenChange: onControlledOpenChange,
    onBlur,
    invalid,
    triggerClassName,
    trigger,
    popupLabel,
    showDisabledAdd,
    includeAccountsLinkedToFinancialAccount = false,
    onCreateAccount,
    onCreateFromInput,
    onCreateFromEmptyInput,
    cellEditor,
}) => {
    const { openOverlay } = useOverlays();

    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : internalIsOpen;
    const setIsOpen = useCallback(
        (value: boolean) => {
            if (!isControlled) {
                setInternalIsOpen(value);
            }
            onControlledOpenChange?.(value);
        },
        [isControlled, onControlledOpenChange],
    );
    const [inputValue, setInputValue] = useState("");

    const { accounts, isLoading } = useAccounts({
        entityId,
        excludeLinkedToFinancialAccount:
            !includeAccountsLinkedToFinancialAccount,
        includeCurrentAccount: true,
        currentAccountId: selectedAccount?.id,
        ledgerBasis,
    });
    const accountsById = useMemo(
        () => new Map(accounts.map((account) => [account.id, account])),
        [accounts],
    );

    const typeFilteredAccounts = useMemo(() => {
        const idFilteredAccounts = allowedAccountIds
            ? accounts.filter((account) => allowedAccountIds.has(account.id))
            : accounts;
        if (!allowedAccountTypes || allowedAccountTypes.length === 0) {
            return idFilteredAccounts;
        }
        return idFilteredAccounts.filter((account) =>
            allowedAccountTypes.some(
                (allowedType) => account.type === allowedType,
            ),
        );
    }, [accounts, allowedAccountIds, allowedAccountTypes]);

    const getAccountLabel = useCallback(
        (account: Account) => displayAccountLabel(account, showAccountCode),
        [showAccountCode],
    );
    const renderSelectedLabel = useCallback(
        () => (selectedAccount ? getAccountLabel(selectedAccount) : ""),
        [selectedAccount, getAccountLabel],
    );

    const filteredAccounts = useMemo(() => {
        if (!inputValue.trim()) {
            return typeFilteredAccounts;
        }
        return typeFilteredAccounts.filter((account) =>
            matchesAccountSearch(account, inputValue),
        );
    }, [typeFilteredAccounts, inputValue]);

    const accountGroups = useMemo(
        () => groupAccounts(filteredAccounts, suggestedType),
        [filteredAccounts, suggestedType],
    );

    const onAccountsCreated = useCallback(
        (newAccounts: Account[]) => {
            const match = newAccounts.find(
                (a) => "entityId" in a && a.entityId === entityId,
            );
            if (
                match &&
                isAccountTypeAllowed(match.type, allowedAccountTypes)
            ) {
                onChange(match);
            }
        },
        [entityId, onChange, allowedAccountTypes],
    );

    // Clear the search input whenever the dropdown closes.
    useEffect(() => {
        if (!isOpen) {
            setInputValue("");
        }
    }, [isOpen]);

    const openCreateOverlay = useCallback(() => {
        const request = buildCreateAccountRequest({
            accountName: inputValue ?? "",
            selectedAccount,
            allowedAccountTypes,
        });
        if (onCreateAccount) {
            onCreateAccount(request);
            return;
        }
        openOverlay<"createCoaAccount">("createCoaAccount", {
            entityIds: [entityId],
            ...request,
            onAccountsCreated,
        });
    }, [
        onCreateAccount,
        openOverlay,
        entityId,
        inputValue,
        selectedAccount,
        allowedAccountTypes,
        onAccountsCreated,
    ]);

    const isInteractive = !readOnly && !disabled;
    const trimmedInputValue = inputValue.trim();
    const isCellSearching =
        cellEditor !== undefined && trimmedInputValue !== "";

    const createFromInput = useCallback(() => {
        if (!onCreateFromInput || trimmedInputValue === "") {
            return;
        }
        onCreateFromInput(trimmedInputValue);
    }, [onCreateFromInput, trimmedInputValue]);

    const createFromEmptyInput = useCallback(() => {
        if (!onCreateFromEmptyInput) {
            return;
        }
        onCreateFromEmptyInput();
    }, [onCreateFromEmptyInput]);

    const isInputEmpty = trimmedInputValue === "";
    const onCreateButtonClick = useCallback(() => {
        if (isInputEmpty && onCreateFromEmptyInput) {
            createFromEmptyInput();
            return;
        }
        if (!onCreateFromInput) {
            openCreateOverlay();
            return;
        }
        createFromInput();
    }, [
        isInputEmpty,
        onCreateFromEmptyInput,
        createFromEmptyInput,
        onCreateFromInput,
        openCreateOverlay,
        createFromInput,
    ]);
    const isCreateButtonDisabled =
        !!onCreateFromInput && isInputEmpty && !onCreateFromEmptyInput;

    // Picker.Search takes no key handler. Base UI lets Enter bubble out of
    // the search only when no row is highlighted (and closes the popup).
    const onSearchKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (
                event.key !== "Enter" ||
                !onCreateFromInput ||
                filteredAccounts.length > 0
            ) {
                return;
            }
            event.preventDefault();
            createFromInput();
        },
        [onCreateFromInput, filteredAccounts.length, createFromInput],
    );

    const cellButtonProps = useMemo(() => ({ tooltip }), [tooltip]);

    return (
        <Picker.Select
            searchable
            cellEditor={cellEditor}
            value={selectedAccount?.id ?? null}
            renderValue={renderSelectedLabel}
            open={isOpen}
            onValueChange={(accountId) => {
                if (!isInteractive) {
                    return;
                }
                if (accountId === null) {
                    onChange(null);
                    return;
                }
                const account = accountsById.get(accountId);
                if (account) {
                    onChange(account);
                }
            }}
            inputValue={inputValue}
            onInputValueChange={setInputValue}
            onOpenChange={setIsOpen}
            onBlur={onBlur}
            aria-invalid={invalid}
            disabled={!isInteractive}
        >
            {trigger ?? (
                <Picker.CellContent
                    addText="Set account"
                    readOnly={disabled}
                    buttonProps={cellButtonProps}
                    showDisabledAdd={showDisabledAdd}
                    className={triggerClassName}
                />
            )}
            <Picker.Popup sideOffset={4} aria-label={popupLabel}>
                <div role="presentation" onKeyDown={onSearchKeyDown}>
                    <Picker.Search placeholder="Search accounts…" />
                </div>
                <Picker.List>
                    {canRevertToDefault && !isCellSearching && (
                        <Picker.Item onSelect={() => onChange(null)}>
                            Revert to default
                        </Picker.Item>
                    )}
                    {canClear && selectedAccount && !isCellSearching && (
                        <Picker.Item onSelect={() => onChange(null)}>
                            Clear selection
                        </Picker.Item>
                    )}
                    {isLoading && <Picker.Loading>Loading…</Picker.Loading>}
                    {!isLoading && accountGroups.length === 0 && (
                        <Picker.Empty>No matching accounts</Picker.Empty>
                    )}
                    {!isLoading &&
                        accountGroups.map((group, index) => (
                            <React.Fragment key={group.type}>
                                {index > 0 && <Picker.Separator />}
                                <Picker.Section>
                                    <Picker.SectionTitle>
                                        {leafGroupHeader.labelOf(
                                            group.accounts[0],
                                        )}
                                    </Picker.SectionTitle>
                                    {group.accounts.map((account) => (
                                        <Picker.Item
                                            key={account.id}
                                            value={account.id}
                                        >
                                            {getAccountLabel(account)}
                                        </Picker.Item>
                                    ))}
                                </Picker.Section>
                            </React.Fragment>
                        ))}
                    {addNewAccountButton && (
                        <Picker.Footer>
                            <Picker.Item
                                leading={<PlusIcon />}
                                onSelect={onCreateButtonClick}
                                disabled={isCreateButtonDisabled}
                            >
                                {inputValue
                                    ? `Create "${inputValue}"`
                                    : "Create new account"}
                            </Picker.Item>
                        </Picker.Footer>
                    )}
                </Picker.List>
            </Picker.Popup>
        </Picker.Select>
    );
};
